<template>
  <HgModal v-model="internalModel" :closeButtonCssClass="$style.closeIcon" @close="closeModal">
    <template #default>
      <div :class="$style.modalContainer">
        <h2 :class="$style.heading">
          {{
            isGotMandateModal
              ? t('sellerLeads.gotMandateModal.title')
              : t('sellerLeads.Sale.salesPrice.title')
          }}
        </h2>
        <p :class="$style.description">
          {{
            isGotMandateModal
              ? t('sellerLeads.gotMandateModal.description')
              : t('sellerLeads.Sale.salesPrice.text')
          }}
        </p>

        <div :class="$style.currencyGroup">
          <HgCurrencyInput
            v-model="targetPrice"
            :locale="HgSupportedLanguage.DE"
            :class="$style.currencyInput"
            :label="
              isGotMandateModal
                ? t('sellerLeads.gotMandateModal.currencyLabel')
                : t('detailsPage.listing.BUY')
            "
          >
          </HgCurrencyInput>
        </div>

        <div :class="$style.currencyGroup">
          <div :class="$style.commissionChoiceGroup">
            <p :class="$style.fieldLabel">
              {{ t('sellerLeads.gotMandateModal.commissionTypeLabel') }}
            </p>
            <div :class="$style.commissionRadioRow">
              <HgRadioButton
                v-model="commissionType"
                name="commissionType"
                inputValue="percentage"
                :label="t('sellerLeads.gotMandateModal.commissionType.percentage')"
              />
              <HgRadioButton
                v-model="commissionType"
                name="commissionType"
                inputValue="fixedFee"
                :label="t('sellerLeads.gotMandateModal.commissionType.amount')"
              />
            </div>
          </div>

          <div v-if="isFixedFeeCommission" :class="$style.currencyGroup">
            <HgCurrencyInput
              v-model="fixedFeeCommission"
              :locale="HgSupportedLanguage.DE"
              :class="$style.currencyInput"
            >
            </HgCurrencyInput>
            <template v-if="hasFixedFeeCommissionError">
              <span :class="$style.errorMessage" data-test-error>
                {{ t('validation.invalidInput') }}
              </span>
            </template>
          </div>

          <div v-else :class="$style.currencyGroup">
            <HgInput
              v-model="percentageCommission"
              updateModelOnInput
              name="percent"
              inputmode="numeric"
              :unit="PERCENTAGE"
              :class="$style.currencyInput"
            >
            </HgInput>
            <template v-if="hasPercentageCommissionError">
              <span :class="$style.errorMessage" data-test-error>
                {{ t('sellerLeads.gotMandateModal.errorMessages.invalidCommission') }}
              </span>
            </template>
          </div>

          <p :class="$style.commissionTypeSubline">
            {{ t('sellerLeads.gotMandateModal.commissionType.subline') }}
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <HgButton
        ref="cancelButton"
        :theme="ButtonTheme.SecondaryBordered"
        :disabled="isLoading"
        @click="closeModal"
      >
        {{ t('sellerLeads.gotMandateModal.cancelButtonText') }}
      </HgButton>
      <HgButton
        ref="submitButton"
        :theme="ButtonTheme.Primary"
        :disabled="!formValid || isLoading"
        @click="submitRequest"
        >{{ t('sellerLeads.gotMandateModal.submitButtonText') }}</HgButton
      >
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { captureError } from '@smg-real-estate/browser-rum';
import {
  ButtonTheme,
  HgButton,
  HgCurrencyInput,
  HgInput,
  HgModal,
  HgRadioButton,
  HgSupportedLanguage,
  type HgCurrencyInputModel,
} from '@smg-real-estate/domus';
import { MandateState, Status } from '@smg-real-estate/seller-leads-schema';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { updateStatus, type OptionalPayload } from '@/api/sellerLeads';
import { ModalIds, type Modal } from '@/components/ModalManager/types';
import { type DisplayableLead } from '@/models/sellerLead';
import { useStore } from 'vuex';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();

const PERCENTAGE = '%';
const CURRENCY = 'CHF';
const CommissionType = {
  Percentage: 'percentage',
  FixedFee: 'fixedFee',
} as const;

type CommissionTypeValue = (typeof CommissionType)[keyof typeof CommissionType];

const targetPrice = ref<HgCurrencyInputModel>({
  price: 0,
  currency: CURRENCY,
});
const percentageCommission = ref<string>('0');
const fixedFeeCommission = ref<HgCurrencyInputModel>({
  price: 0,
  currency: CURRENCY,
});
const commissionType = ref<CommissionTypeValue>(CommissionType.Percentage);
const isLoading = ref<boolean>(false);

const currentModalId = computed<string>(() => {
  const modal = store.getters.getModals.find((item: Modal) => !!item.isOpen);
  return modal ? modal.id : '';
});
const isGotMandateModal = computed<boolean>(
  () => currentModalId.value === ModalIds.GotMandateModal,
);
const isPercentageCommission = computed<boolean>(
  () => commissionType.value === CommissionType.Percentage,
);
const isFixedFeeCommission = computed<boolean>(
  () => commissionType.value === CommissionType.FixedFee,
);

const rules = {
  targetPrice: {
    $autoDirty: true,
    required,
    greaterThanZero: (model: HgCurrencyInputModel) => model.price > 0,
  },
  percentageCommission: {
    $autoDirty: true,
    required: (value: string) => !isPercentageCommission.value || helpers.req(value),
    greaterThanZero: (model: string) => !isPercentageCommission.value || Number(model) > 0,
    maximum: (value: string) => !isPercentageCommission.value || Number(value) <= 100,
    uptoTenDecimal: helpers.withMessage('Enter upto 10 decimal places', (value: string) => {
      if (!isPercentageCommission.value) {
        return true;
      }
      const regex = /^\d+(\.\d{0,10})?$/;
      return regex.test(value.toString());
    }),
    removeLeadingZeros: helpers.withMessage('Invalid commission value', (value: string) => {
      if (!isPercentageCommission.value) {
        return true;
      }
      const parsedValue = value.replace(/[^0-9.e-]/g, '').replace(/^0+(?=\d)/, '');
      if (!Number.isNaN(Number.parseFloat(parsedValue))) {
        percentageCommission.value = parsedValue;
        return true;
      }
      return false;
    }),
  },
  fixedFeeCommission: {
    $autoDirty: true,
    greaterThanZero: (model: HgCurrencyInputModel) =>
      !isFixedFeeCommission.value || model.price > 0,
    maximum: (model: HgCurrencyInputModel) =>
      !isFixedFeeCommission.value || model.price <= targetPrice.value.price,
  },
};

const v$ = useVuelidate(rules, {
  targetPrice,
  percentageCommission,
  fixedFeeCommission,
});

const hasFixedFeeCommissionError = computed<boolean>(
  () =>
    v$.value.fixedFeeCommission.$dirty &&
    (v$.value.fixedFeeCommission.greaterThanZero.$invalid ||
      v$.value.fixedFeeCommission.maximum.$invalid),
);
const hasPercentageCommissionError = computed<boolean>(
  () =>
    v$.value.percentageCommission.$dirty &&
    (v$.value.percentageCommission.maximum.$invalid ||
      v$.value.percentageCommission.greaterThanZero.$invalid ||
      v$.value.percentageCommission.uptoTenDecimal.$invalid ||
      v$.value.percentageCommission.removeLeadingZeros.$invalid),
);

const formValid = computed(() => {
  const activeCommissionDirty = isFixedFeeCommission.value
    ? v$.value.fixedFeeCommission.$dirty
    : v$.value.percentageCommission.$dirty;
  return v$.value.targetPrice.$dirty && activeCommissionDirty && !v$.value.$invalid;
});
const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
      v$.value.$reset();
    }
  },
);

const resetData = () => {
  targetPrice.value = {
    price: 0,
    currency: CURRENCY,
  };
  percentageCommission.value = '0';
  fixedFeeCommission.value = {
    price: 0,
    currency: CURRENCY,
  };
  commissionType.value = CommissionType.Percentage;
  isLoading.value = false;
};

const closeModal = () => {
  const selectedLead: DisplayableLead = store.getters.getSelectedLead;
  store.commit('setSelectedStatus', {
    status: selectedLead.status,
    mandateState: selectedLead.mandateState ?? null,
  });
  store.dispatch('closeModal', { id: currentModalId.value });
};

const submitRequest = async () => {
  const valid = await v$.value.$validate();
  if (!valid) {
    return;
  }

  const payload: OptionalPayload = {
    mandateState: isGotMandateModal.value ? MandateState.OpenMandateWon : MandateState.Sold,
    ...(isGotMandateModal.value
      ? { targetSellingPrice: targetPrice.value.price }
      : { finalSellingPrice: targetPrice.value.price }),
    commissionType: commissionType.value,
    brokerCommission: isPercentageCommission.value
      ? Number.parseFloat(percentageCommission.value.toString())
      : fixedFeeCommission.value.price,
  };

  try {
    const lead: DisplayableLead = store.getters.getSelectedLead;
    isLoading.value = true;
    const response = await updateStatus(
      lead.leadId,
      isGotMandateModal.value ? Status.MandateWon : (lead.status as Status),
      payload,
    );
    isLoading.value = false;
    if (response) {
      store.commit('setSelectedLead', {
        ...lead,
        ...(isGotMandateModal.value && { status: Status.MandateWon }),
        mandateState: payload.mandateState,
        ...(payload.brokerCommission !== undefined && {
          brokerCommission: payload.brokerCommission,
        }),
      });
    } else {
      store.commit('setHasStatusError', true);
    }
  } catch (exception) {
    store.commit('setHasStatusError', true);
    captureError(exception);
  } finally {
    closeModal();
  }
};
</script>

<style module lang="scss">
@import '@/assets/mixins';
@import '@/assets/variables';

.closeIcon {
  visibility: hidden;
}

.modalContainer {
  display: flex;
  flex-direction: column;
  row-gap: $space-200;
  margin-bottom: $space-50;

  * {
    @include vertical-margin;
  }
}

.errorMessage {
  @include body-3;

  & {
    color: var(--validation-error-color);
  }
}

.heading {
  @include heading-2($color-deep-blue-100);
}

.description {
  @include body-2($color-deep-blue-100);
}

.currencyGroup {
  display: flex;
  flex-direction: column;
  row-gap: $space-25;

  fieldset {
    margin-inline: unset;
  }

  [class^='HgCurrencyInput_wrapper_'] {
    flex-direction: row-reverse;

    [class^='HgCurrencyInput_noOptionsLabel_'] {
      padding: 0 0.625rem 0 0;
    }
  }

  :global([class*='HgFormFieldWrapper_noLabel_']) {
    padding-top: $space-75;
  }

  .fieldLabel {
    @include heading-5($color-deep-blue-100);

    font-weight: var(--typo-font-weight-bold);
  }

  .commissionChoiceGroup {
    display: flex;
    flex-direction: column;
    row-gap: $space-25;
  }
}

.commissionRadioRow {
  display: flex;
  flex-wrap: wrap;
  gap: $space-75 $space-250;
}

.commissionTypeSubline {
  @include body-3($color-deep-blue-80);
}
</style>
